import { useCallback, useEffect, useRef, useState } from 'react';
import aviso from '../lib/aviso';
import { INSTAGRAM_AUTH_MESSAGE, InstagramService } from '../services/InstagramService';
import { INSTAGRAM_STATE_KEY } from '../pages/InstagramCallbackPage';

interface Opcoes {
    /** Chamado quando a janela do Instagram volta com sucesso. */
    aoConectar: () => void | Promise<void>;
}

/**
 * Conectar o Instagram pelo login do próprio Instagram, numa janela (o mesmo
 * fluxo de Conexões, 30/09). A janela devolve o resultado por `postMessage`
 * na página de callback; aqui só se abre e se escuta.
 */
export function useConectarInstagram({ aoConectar }: Opcoes) {
    const [conectando, setConectando] = useState(false);
    const aoConectarRef = useRef(aoConectar);
    aoConectarRef.current = aoConectar;

    const abrir = useCallback(async () => {
        setConectando(true);
        try {
            const state = crypto.randomUUID();
            sessionStorage.setItem(INSTAGRAM_STATE_KEY, state);
            const cfg = await InstagramService.getConfiguracaoLoginInstagram(state);
            if (!cfg.sucesso || !cfg.dados?.urlAutorizacao) {
                aviso.erro(cfg.mensagem || 'Configuração do Instagram não carregada.');
                setConectando(false);
                return;
            }
            const popup = window.open(cfg.dados.urlAutorizacao, 'instagram-oauth', 'width=600,height=750,menubar=no,toolbar=no');
            if (!popup) { aviso.atencao('Permita pop-ups para este site e tente novamente.'); setConectando(false); }
        } catch {
            aviso.erro('Não foi possível iniciar a conexão.');
            setConectando(false);
        }
    }, []);

    useEffect(() => {
        const aoReceber = async (e: MessageEvent) => {
            if (e.origin !== window.location.origin) return;
            if (e.data?.type !== INSTAGRAM_AUTH_MESSAGE) return;
            setConectando(false);
            if (e.data.success) { aviso.ok('Instagram conectado.'); await aoConectarRef.current(); }
            else aviso.erro(e.data.mensagem || 'Não foi possível conectar o Instagram.');
        };
        window.addEventListener('message', aoReceber);
        return () => window.removeEventListener('message', aoReceber);
    }, []);

    return { conectando, abrir };
}
